/**
 * Nhãn chức năng bài tập / ôn tập theo vai trò (menu, tiêu đề, SEO, credit).
 */
export type UserRoleLike = 'teacher' | 'parent' | 'student' | string | undefined | null;

/** Menu ngang, breadcrumb, tiêu đề trang khi tạo mới */
export function getHomeworkFeatureLabel(role: UserRoleLike): string {
  switch (role) {
    case 'teacher':
      return 'Giao bài tập về nhà';
    case 'parent':
      return 'Tạo bài ôn tập cho con';
    case 'student':
      return 'Ôn tập & rèn luyện kiến thức';
    default:
      return 'Tạo bài tập';
  }
}

/** Loại tài nguyên trong SEO (buildNamedResourceSeo) */
export function getHomeworkSeoResourceKind(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Bài ôn tập';
    default:
      return 'Bài tập về nhà';
  }
}

/** Dòng ghi nhận tín dụng khi tạo */
export function getHomeworkCreditUsageAction(role: UserRoleLike, topic: string): string {
  const t = topic.trim() || '…';
  switch (role) {
    case 'teacher':
      return `Giao bài tập: ${t}`;
    case 'parent':
      return `Bài ôn tập cho con: ${t}`;
    case 'student':
      return `Ôn tập: ${t}`;
    default:
      return `Tạo bài tập: ${t}`;
  }
}

/** Nút chính bước cuối (tạo) */
export function getHomeworkPrimaryCtaLabel(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return 'Bắt đầu ôn tập';
    case 'parent':
      return 'Tạo bài ôn tập';
    default:
      return 'Bắt đầu tạo bài tập';
  }
}

/** Tiêu đề vùng loading khi AI đang sinh */
export function getHomeworkLoadingHeadline(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return 'AI đang thiết kế bài ôn tập';
    case 'parent':
      return 'AI đang soạn bài ôn tập cho con';
    default:
      return 'AI đang thiết kế bài tập';
  }
}

/** Bước 1: cung cấp nội dung */
export function getHomeworkContentSectionTitle(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return 'Nội dung ôn tập';
    case 'parent':
      return 'Nội dung bài ôn tập';
    default:
      return 'Cung cấp nội dung bài tập';
  }
}

/** Mô tả khối thư viện (tab bài tập) */
export function getHomeworkLibraryBlurb(role: UserRoleLike): string {
  switch (role) {
    case 'teacher':
      return 'Giao bài tập về nhà và theo dõi tiến độ học tập của học sinh.';
    case 'parent':
      return 'Soạn bài ôn tập cho con; lưu trong thư viện và chia sẻ khi cần.';
    case 'student':
      return 'Tạo bài ôn tập để củng cố kiến thức — lưu trong thư viện của bạn.';
    default:
      return 'Tạo và quản lý bài tập trong thư viện.';
  }
}

/** Nút tạo gọn trong thư viện (tránh chuỗi dài) */
export function getHomeworkCompactCreateLabel(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return '+ Ôn tập mới';
    case 'parent':
      return '+ Bài ôn cho con';
    case 'teacher':
      return '+ Giao bài tập';
    default:
      return '+ Tạo bài tập';
  }
}

/** Tiêu đề khi chỉnh sửa bài đã lưu */
export function getHomeworkEditPageTitle(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Chỉnh sửa bài ôn tập';
    default:
      return 'Chỉnh sửa bài tập';
  }
}

/** Nhãn khối hướng dẫn chung (sau khi đã sinh bài) */
export function getHomeworkGeneralInstructionsLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Gợi ý / lưu ý cho con';
    case 'student':
      return 'Ghi chú cho bản thân (tuỳ chọn)';
    default:
      return 'Hướng dẫn / Ghi chú chung';
  }
}

export function getHomeworkGeneralInstructionsPlaceholder(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Ví dụ: khuyến khích con làm từng phần, thời gian dự kiến, cách trình bày...';
    case 'student':
      return 'Ghi chú nhắc nhở khi làm bài (thời gian, cách trình bày...)';
    default:
      return 'Nhập hướng dẫn chung cho bài tập này (ví dụ: yêu cầu về thời gian, cách trình bày...)';
  }
}

/** Gợi ý ngày — ngữ cảnh không phải lớp học */
export function getHomeworkDueDateLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Gợi ý thời gian hoàn thành:';
    case 'student':
      return 'Mục tiêu hoàn thành:';
    default:
      return 'Gợi ý hạn nộp:';
  }
}

/** Checkbox xem kết quả */
export function getHomeworkShowResultLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Cho phép xem điểm và đáp án ngay sau khi nộp bài';
    case 'student':
      return 'Xem điểm và đáp án ngay sau khi hoàn thành';
    default:
      return 'Cho học sinh xem kết quả ngay sau khi nộp';
  }
}

export function getHomeworkTasksSectionTitle(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Nội dung bài (câu hỏi & phần bài)';
    case 'student':
      return 'Các phần bài';
    default:
      return 'Câu hỏi & nội dung bài';
  }
}

export function getHomeworkSavedCardTitle(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Đã lưu bài ôn tập!';
    default:
      return 'Bài tập đã được lưu!';
  }
}

export function getHomeworkSavedLibraryHint(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Bạn có thể mở lại trong «Thư viện của tôi» hoặc gửi cho con làm.';
    case 'student':
      return 'Bạn có thể mở lại trong «Thư viện của tôi» bất cứ lúc nào.';
    default:
      return "Bạn có thể xem lại trong 'Tài liệu của tôi'.";
  }
}

export function getHomeworkBackToHubLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Về trang phụ huynh';
    case 'student':
      return 'Về trang chủ';
    default:
      return 'Về bảng điều khiển';
  }
}

export function getHomeworkDetailedInstructionLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Mô tả mong muốn cho AI (tuỳ chọn)';
    case 'student':
      return 'Gợi ý chi tiết cho AI (tuỳ chọn)';
    default:
      return 'Yêu cầu chi tiết cho AI (Tùy chọn)';
  }
}

export function getHomeworkDetailedInstructionPlaceholder(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Ví dụ: tập trung phần con còn yếu, độ khó vừa phải, thêm ví dụ minh họa...';
    case 'student':
      return 'Ví dụ: ôn phần nào, mức độ khó, thêm ví dụ...';
    default:
      return 'Ví dụ: Tạo câu hỏi về môi trường sống, tập tính của các loài động vật...';
  }
}

export function getHomeworkStep2Title(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Dạng bài & mức độ';
    default:
      return 'Cấu hình dạng bài & Mức độ';
  }
}

export function getHomeworkFormatRequestLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'DẠNG BÀI MONG MUỐN';
    default:
      return 'DẠNG BÀI TẬP YÊU CẦU';
  }
}

/** Dòng phụ dưới progress khi AI đang chạy */
export function getHomeworkLoadingAnalysisLine(role: UserRoleLike, topicPreview: string): string {
  if (topicPreview) {
    return `Chủ đề: ${topicPreview}`;
  }
  switch (role) {
    case 'parent':
      return 'Đang phân tích tài liệu và soạn nội dung phù hợp cho con...';
    case 'student':
      return 'Đang phân tích tài liệu và thiết kế phần bài ôn tập...';
    default:
      return 'Hệ thống đang phân tích tài liệu và thiết kế nhiệm vụ...';
  }
}

export function getHomeworkGenerationTimeNote(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return 'Việc tạo bài có thể mất khoảng 10–20 giây tùy độ dài và độ khó.';
    default:
      return 'Quá trình tạo có thể mất 10-20 giây tùy thuộc vào độ phức tạp của nội dung.';
  }
}

export function getHomeworkShortLoadingButtonLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'AI đang soạn bài...';
    case 'student':
      return 'AI đang tạo bài ôn...';
    default:
      return 'AI đang thiết kế...';
  }
}

export function getHomeworkInitialLoadingMessage(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Đang tải bài ôn tập...';
    default:
      return 'Đang tải bài tập...';
  }
}

/** Lý do nút Tạo bị vô hiệu (tooltip) */
export function getHomeworkDisabledReason(
  role: UserRoleLike,
  sourceMode: 'ai' | 'document' | 'image',
  topicTrim: string,
  contextTrim: string
): string {
  if (sourceMode === 'ai' && !topicTrim) {
    if (role === 'student') return 'Vui lòng nhập chủ đề ôn tập';
    if (role === 'parent') return 'Vui lòng nhập chủ đề hoặc chủ điểm';
    return 'Vui lòng nhập chủ đề chính';
  }
  if (sourceMode === 'document' && !topicTrim && !contextTrim) {
    if (role === 'student') return 'Vui lòng thêm tài liệu hoặc nhập chủ đề ôn tập';
    if (role === 'parent') return 'Vui lòng thêm tài liệu hoặc nhập chủ đề';
    return 'Vui lòng cung cấp tài liệu hoặc nhập chủ đề chính';
  }
  if (sourceMode === 'image' && !topicTrim && !contextTrim) {
    return 'Hãy tải ảnh và bấm «Phân tích & điền dữ liệu», hoặc nhập chủ đề';
  }
  return '';
}

/** Chuỗi gợi ý ngày khi lưu (kèm preset hạn) */
export function getHomeworkDueDateSuggestionLine(date: Date, role: UserRoleLike): string {
  const s = date.toLocaleDateString('vi-VN');
  switch (role) {
    case 'parent':
      return `Dự kiến hoàn thành trước ${s}`;
    case 'student':
      return `Mục tiêu hoàn thành trước ${s}`;
    default:
      return `Hạn nộp vào ${s}`;
  }
}

export function getHomeworkSaveSuccessNotificationMessage(savedTitle: string, role: UserRoleLike): string {
  const t = savedTitle.trim() || 'Bài';
  switch (role) {
    case 'parent':
    case 'student':
      return `Bài ôn tập «${t}» đã được lưu.`;
    default:
      return `Bài tập "${t}" đã được lưu thành công.`;
  }
}

export function getHomeworkUpdateSuccessMessage(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Đã cập nhật bài ôn tập.';
    default:
      return 'Bài tập đã được cập nhật thành công!';
  }
}

/** Tab nguồn — chữ ngắn gọn */
export function getHomeworkSourceTabAiLabel(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
    case 'student':
      return 'Gợi ý từ AI';
    default:
      return 'AI Tự động Tạo';
  }
}

/** Đoạn giới thiệu tab AI */
export function getHomeworkAiTabIntro(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'AI sẽ gợi ý và soạn nội dung theo chủ đề và mô tả bạn nhập bên dưới.';
    case 'student':
      return 'AI sẽ gợi ý nội dung ôn tập theo chủ đề và gợi ý bạn nhập bên dưới.';
    default:
      return 'AI sẽ tự động nghiên cứu và tạo nội dung dựa trên thông tin bạn cung cấp bên dưới.';
  }
}

/** Tab tải ảnh (biến thể homework) — một đoạn văn */
/** Nhãn ô chủ đề (bước 1) */
export function getHomeworkTopicFieldLabel(role: UserRoleLike): string {
  switch (role) {
    case 'student':
      return 'Chủ đề ôn tập';
    case 'parent':
      return 'Chủ đề hoặc chủ điểm';
    default:
      return 'Chủ đề chính';
  }
}

export function getHomeworkImageTabHomeworkBlurb(role: UserRoleLike): string {
  switch (role) {
    case 'parent':
      return 'Kéo thả hoặc chọn ảnh để tải lên thư viện, hoặc chọn ảnh có sẵn trong Thư viện của bạn (có ô tìm kiếm). Chọn một ảnh rồi bấm Phân tích & điền dữ liệu để AI nhận diện cấu trúc và nội dung, sau đó tạo bài ôn tập mới cùng dạng.';
    case 'student':
      return 'Kéo thả hoặc chọn ảnh để tải lên thư viện, hoặc chọn ảnh có sẵn. Chọn một ảnh rồi bấm Phân tích & điền dữ liệu để AI nhận diện nội dung và tạo bài ôn tập mới cùng dạng.';
    default:
      return 'Kéo thả hoặc chọn ảnh để tải lên thư viện, hoặc chọn ảnh có sẵn trong Thư viện của bạn (có ô tìm kiếm). Chọn một ảnh rồi bấm Phân tích & điền dữ liệu để AI nhận diện cấu trúc và nội dung, sau đó tạo bài tập mới cùng dạng.';
  }
}
